import React from 'react';
import PropTypes from 'prop-types';
import { LuCalendarClock, LuSparkles, LuUsersRound } from 'react-icons/lu';
import useTodoText from '../todoText';
import { isOverdue } from '../useTodoList';
import { OverdueBadge, PRIORITY_META, PriorityBadge, StatusBadge, StatusSelect } from './taskBits';

const MUTED = 'tw-text-slate-400 dark:tw-text-slate-500';

/** Tooltip of a cell (full title and description, every assignee, …). */
export const useTaskCellTitle = () => {
  const { assigneeNames, creatorLabel, formatDate } = useTodoText();
  return (task, key) => {
    if (key === 'title') return [task.title, task.description].filter(Boolean).join('\n\n') || undefined;
    if (key === 'assignee') return assigneeNames(task, 0) || undefined;
    if (key === 'createdBy') return creatorLabel(task.createdBy) || undefined;
    if (key === 'dueDate') return formatDate(task.dueDate) || undefined;
    return undefined;
  };
};

/** Content of one task table cell. */
const TaskCell = ({ columnKey, task, canChange, savingStatus, onStatusChange }) => {
  const { formatDate, assigneeNames, creatorLabel } = useTodoText();
  const overdue = isOverdue(task);
  const done = task.status === 'completed' || task.status === 'cancelled';

  switch (columnKey) {
    case 'title':
      return (
        <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2.5">
          <span aria-hidden="true" className={`tw-h-7 tw-w-1 tw-shrink-0 tw-rounded-full ${PRIORITY_META[task.priority]?.bar || 'tw-bg-slate-300'}`} />
          <span className="tw-min-w-0">
            <span className={`tw-flex tw-min-w-0 tw-items-center tw-gap-1.5 tw-font-semibold ${done ? 'tw-text-slate-500 dark:tw-text-slate-400' : 'tw-text-slate-900 dark:tw-text-white'}`}>
              <span className="tw-truncate">{task.title || '-'}</span>
              {task.autoCreated && <LuSparkles aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-violet-500" />}
            </span>
            {task.description && <span className="tw-block tw-truncate tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{task.description}</span>}
          </span>
        </span>
      );
    case 'status':
      return canChange
        ? <StatusSelect status={task.status || 'pending'} busy={savingStatus} onChange={(value) => onStatusChange(task.id, value)} />
        : <StatusBadge status={task.status} />;
    case 'priority':
      return <PriorityBadge priority={task.priority} />;
    case 'assignee': {
      const names = assigneeNames(task);
      return names ? (
        <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-1.5">
          <LuUsersRound aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-slate-400" />
          <span className="tw-truncate">{names}</span>
        </span>
      ) : <span className={MUTED}>-</span>;
    }
    case 'dueDate': {
      const text = formatDate(task.dueDate);
      if (!text) return <span className={MUTED}>-</span>;
      return (
        <span className="tw-flex tw-items-center tw-gap-1.5">
          <span className={overdue ? 'tw-font-semibold tw-text-rose-600 dark:tw-text-rose-400' : ''}>{text}</span>
          {overdue && <OverdueBadge />}
        </span>
      );
    }
    case 'createdAt':
      return formatDate(task.createdAt) ? <span className="tw-tabular-nums">{formatDate(task.createdAt)}</span> : <span className={MUTED}>-</span>;
    case 'createdBy':
      return creatorLabel(task.createdBy) ? <span className="tw-block tw-truncate">{creatorLabel(task.createdBy)}</span> : <span className={MUTED}>-</span>;
    default:
      return null;
  }
};

TaskCell.propTypes = {
  columnKey: PropTypes.string.isRequired,
  task: PropTypes.object.isRequired,
  canChange: PropTypes.bool,
  savingStatus: PropTypes.bool,
  onStatusChange: PropTypes.func.isRequired,
};

/** One row of the card list (narrow screens). */
export const TaskCardRow = ({ task }) => {
  const { formatDate, assigneeNames } = useTodoText();
  const overdue = isOverdue(task);
  const names = assigneeNames(task);
  const due = formatDate(task.dueDate);
  return (
    <>
      <span aria-hidden="true" className={`tw-w-1.5 tw-shrink-0 tw-self-stretch tw-rounded-full ${PRIORITY_META[task.priority]?.bar || 'tw-bg-slate-300'}`} />
      <span className="tw-min-w-0 tw-flex-1">
        <span className="tw-line-clamp-2 tw-text-[15px] tw-font-semibold tw-leading-snug tw-text-slate-900 dark:tw-text-white">{task.title || '-'}</span>
        {(names || due) && (
          <span className="tw-mt-1 tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
            {names && (
              <span className="tw-inline-flex tw-min-w-0 tw-items-center tw-gap-1">
                <LuUsersRound aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />
                <span className="tw-truncate">{names}</span>
              </span>
            )}
            {due && (
              <span className={`tw-inline-flex tw-items-center tw-gap-1 ${overdue ? 'tw-font-semibold tw-text-rose-600 dark:tw-text-rose-400' : ''}`}>
                <LuCalendarClock aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />
                {due}
              </span>
            )}
          </span>
        )}
        <span className="tw-mt-2 tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
          <StatusBadge status={task.status} />
          <PriorityBadge priority={task.priority} />
          {overdue && <OverdueBadge />}
        </span>
      </span>
    </>
  );
};

TaskCardRow.propTypes = { task: PropTypes.object.isRequired };

export default TaskCell;
